import { useState, useRef, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Send, ImageIcon, X } from "lucide-react";
import VoiceOrb from "./VoiceOrb";

interface Attachment {
  type: "image";
  data: string;
  name: string;
}

interface AgentInputProps {
  onSend: (text: string, attachments?: Attachment[]) => void;
  isLoading: boolean;
}

const AgentInput = ({ onSend, isLoading }: AgentInputProps) => {
  const [text, setText] = useState("");
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const fileRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = () => {
    const trimmed = text.trim();
    if (!trimmed && attachments.length === 0) return;
    onSend(trimmed, attachments.length > 0 ? attachments : undefined);
    setText("");
    setAttachments([]);
    if (textareaRef.current) textareaRef.current.style.height = "auto";
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    Array.from(files).forEach(file => {
      if (!file.type.startsWith("image/")) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        setAttachments(prev => [...prev, { type: "image", data: reader.result as string, name: file.name }]);
      };
      reader.readAsDataURL(file);
    });
    if (fileRef.current) fileRef.current.value = "";
  };

  const handlePaste = useCallback((e: React.ClipboardEvent) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    for (const item of Array.from(items)) {
      if (item.type.startsWith("image/")) {
        e.preventDefault();
        const file = item.getAsFile();
        if (!file) continue;
        const reader = new FileReader();
        reader.onloadend = () => {
          setAttachments(prev => [...prev, { type: "image", data: reader.result as string, name: "pasted-image.png" }]);
        };
        reader.readAsDataURL(file);
      }
    }
  }, []);

  const removeAttachment = (idx: number) => {
    setAttachments(prev => prev.filter((_, i) => i !== idx));
  };

  const handleTranscription = (transcribedText: string) => {
    setText(prev => (prev ? prev + " " + transcribedText : transcribedText));
    textareaRef.current?.focus();
  };

  const handleTextareaInput = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setText(e.target.value);
    const ta = e.target;
    ta.style.height = "auto";
    ta.style.height = Math.min(ta.scrollHeight, 160) + "px";
  };

  return (
    <div className="border-t border-border bg-background/90 backdrop-blur-md p-3 sm:p-4">
      {/* Attachment previews */}
      {attachments.length > 0 && (
        <div className="flex gap-2 mb-2 overflow-x-auto pb-1">
          {attachments.map((att, i) => (
            <div key={i} className="relative flex-shrink-0">
              <img src={att.data} alt={att.name} className="h-14 w-14 object-cover rounded-lg ink-border" />
              <button
                onClick={() => removeAttachment(i)}
                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-destructive text-white flex items-center justify-center"
              >
                <X className="h-2.5 w-2.5" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-end gap-2">
        {/* File upload */}
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={handleFileChange} />
        <button
          type="button"
          className="h-10 w-10 flex-shrink-0 flex items-center justify-center rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors disabled:opacity-50"
          onClick={() => fileRef.current?.click()}
          disabled={isLoading}
        >
          <ImageIcon className="h-5 w-5" />
        </button>

        {/* Text input */}
        <div className="flex-1">
          <textarea
            ref={textareaRef}
            rows={1}
            value={text}
            onChange={handleTextareaInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder="Paste a testimonial, drop a screenshot, or just ask…"
            className="w-full resize-none rounded-xl border border-border bg-muted/30 px-4 py-2.5 text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-brand-500 focus:border-brand-500 max-h-40"
            disabled={isLoading}
          />
        </div>

        {/* Voice */}
        <VoiceOrb onTranscription={handleTranscription} isDisabled={isLoading} />

        {/* Send */}
        <Button
          onClick={handleSubmit}
          disabled={isLoading || (!text.trim() && attachments.length === 0)}
          className="h-10 w-10 p-0 flex-shrink-0 rounded-lg gradient-brand text-white shadow-brand disabled:opacity-40"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

export default AgentInput;
